/**
 * Voice Responder — The brain that answers patient questions.
 * Takes a classified intent + drug data → generates a natural spoken response.
 * Speaks the response via TTS and shows a notification.
 *
 * This is the CRITICAL missing piece: without this, the voice button captures
 * speech but never responds. This handler listens for transcript events,
 * classifies the intent, looks up drug data, and speaks the answer.
 */

import { classifyIntent } from './voice-router';
import { speak, stop as stopSpeech } from './tts';
import { getDrugById, searchDrugsByName, getAllDrugs } from '../core/drug-store';
import { getPatientMedications, logDose, getPatientName } from '../core/db';
import { showNotification } from '../ui/components/notification-card';
import type { Drug, VoiceIntent } from '../types/index';
import { queryRAG } from '../core/rag-pipeline';
import { isLLMReady } from '../core/llm-engine';
import { logAction } from '../core/audit-log';
import { t } from '../i18n';

// -----------------------------------------------------------------------
// Drug lookup helpers
// -----------------------------------------------------------------------

/**
 * Try to find a drug by name from the patient's medication list first,
 * then fall back to the full drug database.
 */
async function findDrug(drugName: string | undefined): Promise<Drug | null> {
  if (!drugName) return null;

  const lower = drugName.toLowerCase();

  // Check patient's own medications first
  const patientMeds = getPatientMedications();
  for (const med of patientMeds) {
    if (med.drug_name.toLowerCase().includes(lower)) {
      // Find the full drug info
      const drug = await getDrugById(med.drug_rxcui);
      if (drug) return drug;
    }
  }

  // Search the full database
  const results = await searchDrugsByName(drugName);
  return results[0] ?? null;
}

/**
 * Get the "current drug" — either from the intent, or the patient's first medication.
 */
async function getCurrentDrug(intent: VoiceIntent): Promise<Drug | null> {
  if (intent.drug_name) {
    return findDrug(intent.drug_name);
  }

  // If no drug named, use the patient's first active medication
  const patientMeds = getPatientMedications();
  if (patientMeds.length > 0) {
    return getDrugById(patientMeds[0].drug_rxcui);
  }

  return null;
}

// -----------------------------------------------------------------------
// Response generators — natural, warm, nurse-like responses
// -----------------------------------------------------------------------

function respondDrugInfo(drug: Drug): string {
  const name = drug.generic_name;
  let response = `${name}. `;
  if (drug.description) {
    response += drug.description + ' ';
  }
  if (drug.dosage_instructions) {
    response += t('voice.drugInfoHowToTake', { instructions: drug.dosage_instructions });
  }
  return response;
}

function respondSideEffects(drug: Drug): string {
  if (drug.side_effects.length === 0) {
    return t('voice.noSideEffects', { drug: drug.generic_name });
  }
  const effects = drug.side_effects.slice(0, 5).join(', ');
  return t('voice.sideEffects', { drug: drug.generic_name, effects });
}

function respondFoodInteractions(drug: Drug): string {
  if (drug.food_interactions.length === 0) {
    return t('voice.noFoodInteractions', { drug: drug.generic_name });
  }
  const foods = drug.food_interactions.join(', ');
  return t('voice.foodInteractions', { drug: drug.generic_name, foods });
}

function respondMissedDose(drug: Drug): string {
  if (drug.missed_dose) {
    return t('voice.missedDose', { drug: drug.generic_name, advice: drug.missed_dose });
  }
  return t('voice.missedDoseFallback', { drug: drug.generic_name });
}

function respondStorage(drug: Drug): string {
  if (drug.storage) {
    return t('voice.storage', { drug: drug.generic_name, info: drug.storage });
  }
  return t('voice.storageFallback', { drug: drug.generic_name });
}

function respondWarnings(drug: Drug): string {
  if (drug.warnings.length === 0) {
    return t('voice.noWarnings', { drug: drug.generic_name });
  }
  const warnings = drug.warnings.slice(0, 3).join('. ');
  return t('voice.warnings', { drug: drug.generic_name, warnings });
}

function respondWhyTaking(drug: Drug): string {
  if (drug.description) {
    return t('voice.whyTaking', { drug: drug.generic_name, reason: drug.description.charAt(0).toLowerCase() + drug.description.slice(1) });
  }
  return t('voice.whyTakingFallback', { drug: drug.generic_name });
}

function respondHowWorks(drug: Drug): string {
  if (drug.description) {
    return t('voice.howWorks', { drug: drug.generic_name, description: drug.description });
  }
  return t('voice.howWorksFallback', { drug: drug.generic_name });
}

async function respondNextDose(): Promise<string> {
  const meds = getPatientMedications();
  if (meds.length === 0) {
    return t('voice.noMedsForDose');
  }

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  let nextMed = '';
  let nextTime = '';
  let bestDiff = Infinity;

  for (const med of meds) {
    for (const timeStr of med.times) {
      const [h, m] = timeStr.split(':').map(Number);
      const medMinutes = h * 60 + m;
      let diff = medMinutes - nowMinutes;
      if (diff < -30) diff += 1440;
      if (diff >= 0 && diff < bestDiff) {
        bestDiff = diff;
        nextMed = med.drug_name;
        nextTime = timeStr;
      }
    }
  }

  if (!nextMed) {
    return t('voice.allDoneToday');
  }

  const [h, m] = nextTime.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  const timeFormatted = `${hour12}:${String(m).padStart(2, '0')} ${period}`;

  if (bestDiff <= 0) {
    return t('voice.takeNow', { drug: nextMed });
  }
  if (bestDiff < 60) {
    return t('voice.nextIn', { drug: nextMed, n: String(bestDiff), time: timeFormatted });
  }
  const hrs = Math.floor(bestDiff / 60);
  return t('voice.nextAt', { drug: nextMed, time: timeFormatted, n: String(hrs), s: hrs > 1 ? 's' : '' });
}

async function respondSchedule(): Promise<string> {
  const meds = getPatientMedications();
  if (meds.length === 0) {
    return t('voice.noMedsForDose');
  }

  const parts = [t('voice.scheduleCount', { n: String(meds.length), s: meds.length > 1 ? 's' : '' })];
  for (const med of meds) {
    const times = med.times.map(tm => {
      const [h, m] = tm.split(':').map(Number);
      const period = h >= 12 ? 'PM' : 'AM';
      return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${period}`;
    }).join(t('voice.andJoiner'));
    parts.push(t('voice.scheduleItem', { drug: med.drug_name, times }));
  }
  return parts.join(' ');
}

function respondLogTaken(drug: Drug | null, drugName: string | undefined): string {
  const name = drug?.generic_name ?? drugName ?? t('voice.yourMedication');

  // Log the dose
  const meds = getPatientMedications();
  const matchedMed = meds.find(m =>
    m.drug_name.toLowerCase().includes((drugName ?? '').toLowerCase())
  );
  if (matchedMed) {
    logDose(matchedMed.id, 'taken');
  }

  return t('voice.loggedTaken', { drug: name });
}

function respondLogSkipped(drug: Drug | null, drugName: string | undefined): string {
  const name = drug?.generic_name ?? drugName ?? t('voice.yourMedication');

  const meds = getPatientMedications();
  const matchedMed = meds.find(m =>
    m.drug_name.toLowerCase().includes((drugName ?? '').toLowerCase())
  );
  if (matchedMed) {
    logDose(matchedMed.id, 'skipped');
  }

  return t('voice.loggedSkipped', { drug: name });
}

function respondHelp(): string {
  return t('voice.help');
}

function respondUnknown(): string {
  return t('voice.unknown');
}

function respondGraceful(topic: string): string {
  return t('voice.graceful', { topic });
}

// -----------------------------------------------------------------------
// Main response handler
// -----------------------------------------------------------------------

async function generateResponse(intent: VoiceIntent): Promise<string> {
  const drug = await getCurrentDrug(intent);
  const patientName = getPatientName();

  // Route ALL questions through RAG pipeline when LLM is available.
  // Only fast-path intents (next_dose, my_medications, schedule, log_taken, log_skipped)
  // bypass the LLM because they need exact data, not AI interpretation.
  const TEMPLATE_ONLY: Set<string> = new Set([
    'next_dose', 'my_medications', 'schedule', 'log_taken', 'log_skipped',
    'show_emr', 'help',
  ]);

  // For non-template intents, route to RAG pipeline.
  // RAG has its own template tier for when LLM isn't ready yet.
  if (!TEMPLATE_ONLY.has(intent.type)) {
    try {
      const { embed } = await import('../core/embedding-engine');
      if (typeof embed === 'function') {
        const queryVector = await embed(intent.raw_text);
        if (queryVector) {
          const ragResult = await queryRAG(intent.raw_text, queryVector, undefined, intent.type);
          logAction('voice_llm_query', JSON.stringify({ intent: intent.type, question: (intent.raw_text || '').slice(0, 100) }));
          return ragResult.response;
        }
      }
    } catch (ragErr) {
      console.warn('[VoiceResponder] RAG failed, using template:', ragErr);
    }

    // If RAG failed and LLM not ready, show loading message
    if (!isLLMReady()) {
      return t('voice.gettingReady');
    }
  }

  switch (intent.type) {
    case 'next_dose':
      return respondNextDose();

    case 'drug_info':
      if (!drug) return t('voice.drugNotFound', { drug: intent.drug_name ?? t('voice.thatMedication') });
      return respondDrugInfo(drug);

    case 'side_effects':
    case 'side_effect_concern':
      if (!drug) return t('voice.whichMed');
      return respondSideEffects(drug);

    case 'food_interactions':
      if (!drug) return t('voice.whichMedFood');
      return respondFoodInteractions(drug);

    case 'missed_dose':
      if (!drug) return respondMissedDose({ generic_name: t('voice.yourMedication'), missed_dose: '' } as Drug);
      return respondMissedDose(drug);

    case 'storage':
      if (!drug) return t('voice.whichMedStorage');
      return respondStorage(drug);

    case 'interactions':
      if (!drug) return t('voice.whichMedInteractions');
      return respondWarnings(drug);

    case 'why_taking':
      if (!drug) return t('voice.whichMedWhy');
      return respondWhyTaking(drug);

    case 'how_works':
      if (!drug) return t('voice.whichMedHow');
      return respondHowWorks(drug);

    case 'log_taken':
      return respondLogTaken(drug, intent.drug_name);

    case 'log_skipped':
      return respondLogSkipped(drug, intent.drug_name);

    case 'show_schedule':
      return respondSchedule();

    case 'help':
      return respondHelp();

    case 'pill_identify':
      if (!drug) return t('voice.whichMedPillId');
      return t('voice.pillIdentify', { drug: drug.generic_name, color: drug.pill_color || '', shape: drug.pill_shape || t('voice.pill'), imprint: drug.pill_imprint ? t('voice.pillImprint', { imprint: drug.pill_imprint }) : '' });

    case 'overdose':
      return respondGraceful(t('voice.topicOverdose'));

    case 'pregnancy':
      return respondGraceful(t('voice.topicPregnancy'));

    case 'alcohol':
      if (drug) return t('voice.alcohol', { drug: drug.generic_name });
      return respondGraceful(t('voice.topicAlcohol'));

    case 'driving':
      if (drug) return t('voice.driving', { drug: drug.generic_name });
      return respondGraceful(t('voice.topicDriving'));

    case 'stop_taking':
      return t('voice.stopTaking');

    case 'generic_vs_brand':
      if (drug && drug.brand_names.length > 0) {
        return t('voice.genericVsBrand', { drug: drug.generic_name, brands: drug.brand_names.join(', ') });
      }
      return respondGraceful(t('voice.topicGenericVsBrand'));

    case 'cost':
      return respondGraceful(t('voice.topicCost'));

    case 'add_medication':
      return t('voice.addMedication');

    case 'unknown':
    default:
      return respondUnknown();
  }
}

// -----------------------------------------------------------------------
// Event handler — connects transcript events to the response system
// -----------------------------------------------------------------------

/**
 * Set up the voice response system. Call this once during app init.
 * Listens for 'aftercare:transcript' events and generates spoken responses.
 */
export function setupVoiceResponder(): void {
  document.addEventListener('aftercare:transcript', async (e: Event) => {
    const customEvent = e as CustomEvent<{ text: string }>;
    const text = customEvent.detail?.text?.trim();

    if (!text) {
      speak(t('voice.didntHear'));
      return;
    }

    console.log('[VoiceResponder] Heard:', text);
    showNotification(t('voice.youSaid', { text }), 'info');

    try {
      // Classify the intent
      const intent = await classifyIntent(text);
      console.log('[VoiceResponder] Intent:', intent.type, 'Drug:', intent.drug_name, 'Confidence:', intent.confidence.toFixed(2));

      // Generate and speak the response
      const response = await generateResponse(intent);
      console.log('[VoiceResponder] Response:', response.substring(0, 100) + '...');

      stopSpeech();
      await speak(response);

      // After speaking, auto-listen again for follow-up questions
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('aftercare:voice-start'));
      }, 500);

    } catch (err) {
      console.error('[VoiceResponder] Error:', err);
      speak(t('voice.somethingWrong'));
    }
  });

  console.log('[VoiceResponder] Voice response system ready');
}
